import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useState, useEffect } from "react";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";
import { ActiveTest } from "../test/ActiveTest";
import { EditTestDialog } from "../test/EditTestDialog";
import { CreateTestDialog } from "@/components/car-test/CreateTestDialog";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { useTheme } from "@/context/ThemeContext";
import CreateTestButton from "@/components/car-test/CreateTestButton";
import {
  CarTestSortSelect,
  type CarTestListSort,
} from "@/components/car-test/CarTestSortSelect";
import { Archive, Check, ListChecks } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AnimatePresence, motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ColoredOutlineButton,
  OUTLINE_TEXT_BUTTON_ICON_CLASS,
} from "@/components/ui/colored-outline-button";
import {
  generateVehicleTestPdf,
  resolveTestImageUrls,
} from "@/utils/pdfGenerator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { VehicleTestDocumentView } from "./VehicleTestDocumentView";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useNativeSheetOpensExpanded } from "@/lib/native/useNativeSheetOpensExpanded";
import { cn } from "@/lib/utils";
import { useTest } from "@/hooks/useTest";
import { TestService } from "@/services/test/testService";
import { useMedia } from "@/hooks/useMedia";
import { VehicleTest } from "@/types/vehicleTest";
import { toast } from "sonner";
import { PdfGenerationDialog } from "@/components/ui/PdfGenerationDialog";
import { CreateListingDialog } from "../listings/CreateListingDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useShowNavigateBack } from "@/hooks/useShowNavigateBack";
import { useCurrentAdminPermissions } from "@/hooks/useCurrentAdminPermissions";
import { useNavigate } from "react-router-dom";
import { Pagination } from "@/components/ui/pagination";
import { carTestLabels } from "@/i18n/labels";
import { useLanguage } from "@/context/LanguageContext";

function mapVehicleTestToDocumentData(test: VehicleTest) {
  return test;
}

const AdminCarTest = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const [selectedTest, setSelectedTest] = useState<VehicleTest | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("vehicle");
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const isMobile = useIsMobile();
  const { isNative: isNativeApp } = useNativePlatform();
  const showPageBack = useShowNavigateBack();
  const { canViewBidsOverview, canManageVehicles } =
    useCurrentAdminPermissions();
  const [animationStatus, setAnimationStatus] = useState<{
    [id: string]: boolean;
  }>({});
  const [viewTest, setViewTest] = useState<VehicleTest | null>(null);
  useNativeSheetOpensExpanded(!!viewTest, isNativeApp);
  const [complementDialog, setComplementDialog] = useState<{
    open: boolean;
    email: string;
    test: VehicleTest | null;
  }>({ open: false, email: "", test: null });
  const [complementMsg, setComplementMsg] = useState("");
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<{
    isOpen: boolean;
    testName?: string;
    fileName?: string;
  }>({
    isOpen: false,
  });
  const [handledStatus, setHandledStatus] = useState<
    "all" | "approved" | "rejected" | "active-listing"
  >("all");
  const [activeStatus, setActiveStatus] = useState<
    "all" | "pending-review" | "complement" | "change-requested"
  >("all");
  const [testTab, setTestTab] = useState<"active" | "handled" | "draft">(
    "active",
  );
  const [selectedTests, setSelectedTests] = useState<Set<string>>(new Set());
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [isArchiveMode, setIsArchiveMode] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize(
    "pageSize:adminCarTests",
    10,
    [10, 25, 50],
  );
  const [searchQuery, setSearchQuery] = useState(""); // Debounced search query
  const [searchInput, setSearchInput] = useState(""); // Immediate search input
  const [testSort, setTestSort] = useState<CarTestListSort>("-updatedAt");

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // CreateListingDialog state
  const [showCreateListing, setShowCreateListing] = useState(false);
  const [selectedVehicleForListing, setSelectedVehicleForListing] =
    useState<VehicleTest | null>(null);

  // Use the test hook to fetch data - pass the active tab, page, limit, and search
  const {
    adminTests,
    totalAdminTests,
    updateTestStatus,
    isUpdatingTestStatus,
    isLoadingAdminTests,
    adminTestsError,
    refetchAdminTests,
    archiveCarTests,
    isArchivingCarTests,
  } = useTest({
    tab: testTab,
    page: currentPage,
    limit: itemsPerPage,
    search: searchQuery || undefined,
    sort: testSort,
  });

  const { getDownloadUrlAsync } = useMedia();

  // Draft tests are now handled by the useTest hook via React Query
  const draftTests = testTab === "draft" ? adminTests : null;
  const totalDraftTests = testTab === "draft" ? totalAdminTests : 0;
  const isLoadingDraftTests = testTab === "draft" ? isLoadingAdminTests : false;

  const [activeTests, setActiveTests] = useState<VehicleTest[] | null>(null);
  const [handledTests, setHandledTests] = useState<VehicleTest[] | null>(null);

  // Client-side status filtering (search is now server-side)
  const filterTestByStatus = (
    test: VehicleTest,
    statusFilter:
      | "all"
      | "pending-review"
      | "complement"
      | "change-requested"
      | "approved"
      | "rejected"
      | "active-listing",
  ) => {
    return statusFilter === "all" || test.status === statusFilter;
  };

  useEffect(() => {}, []);

  // useEffect to populate activeTest and handledTests based on API data
  // Since data is now pre-filtered by backend, we just need to set it based on the active tab
  useEffect(() => {
    if (testTab === "active") {
      // For active tab, data already contains only pending-review and complement
      if (adminTests && adminTests.length > 0) {
        setActiveTests(adminTests);
      } else {
        setActiveTests(null);
      }
      setHandledTests(null);
    } else if (testTab === "handled") {
      // For handled tab, data already contains approved, rejected
      if (adminTests && adminTests.length > 0) {
        setHandledTests(adminTests);
      } else {
        setHandledTests(null);
      }
      setActiveTests(null);
    } else if (testTab === "draft") {
      setActiveTests(null);
      setHandledTests(null);
      // Draft tests are now handled by useTest hook via React Query
    }
  }, [adminTests, testTab]);

  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [testToReject, setTestToReject] = useState<VehicleTest | null>(null);

  const handleStatusChange = async (
    test: VehicleTest,
    status: "approved" | "rejected" | "complement",
    notes?: string,
    rejectionReason?: string,
  ) => {
    try {
      await updateTestStatus({
        id: test.id,
        updateData: {
          status: status,
          notes: notes,
          rejectionReason: rejectionReason,
        },
      });

      // Start animation after successful update
      // INFO: Commented off this and everything works still as expected
      // setAnimationStatus((prev) => ({ ...prev, [test.id]: true }));
      // setTimeout(() => {
      //   setActiveTests((prev) =>
      //     prev.filter(
      //       (t) => t.vehicleDetails.regNumber !== test.vehicleDetails.regNumber
      //     )
      //   );
      //   setHandledTests((prev) => [{ ...test, status }, ...prev]);
      //   setAnimationStatus((prev) => {
      //     const copy = { ...prev };
      //     delete copy[test.vehicleDetails.regNumber];
      //     return copy;
      //   });
      // }, 300);
    } catch (error) {
      toast.error(ct.messages.updateStatusError);
    }
  };

  const handleRejectClick = (test: VehicleTest) => {
    setTestToReject(test);
    setShowRejectDialog(true);
  };

  const handleRejectConfirm = async () => {
    if (testToReject && rejectReason.trim()) {
      await handleStatusChange(
        testToReject,
        "rejected",
        undefined,
        rejectReason.trim(),
      );
      setShowRejectDialog(false);
      setRejectReason("");
      setTestToReject(null);
      toast.success(ct.messages.testRejected);
    }
  };

  const handleEdit = (test: VehicleTest) => {
    setSelectedTest(test);
    setIsEditDialogOpen(true);
  };

  const handleCreateTest = () => {
    setIsCreateDialogOpen(true);
  };

  const handleCreateAdFromTest = (test: VehicleTest) => {
    setSelectedVehicleForListing(test);
    setShowCreateListing(true);
  };

  const handleCreateListingSuccess = () => {
    if (selectedVehicleForListing) {
      const updatedTest = {
        ...selectedVehicleForListing,
        status: "created" as const,
      };
      setHandledTests((prev) =>
        prev
          ? prev.map((test) =>
              test.id === selectedVehicleForListing.id ? updatedTest : test,
            )
          : null,
      );
    }
    setShowCreateListing(false);
    setSelectedVehicleForListing(null);
  };

  const handleConfirmChangeRequest = async (test: VehicleTest) => {
    try {
      await TestService.confirmChangeRequest(test.id);
      refetchAdminTests();
      toast.success(
        ct.messages?.changeRequestConfirmed ??
          "Åtgärd bekräftad. Användaren har notifierats.",
      );
    } catch (error) {
      toast.error(
        ct.messages?.confirmChangeRequestError ?? "Kunde inte bekräfta åtgärd.",
      );
    }
  };

  // Show loading state
  if (isLoadingAdminTests) {
    return (
      <div>
        {showPageBack && <BtnNavigateBack label="Dashboard" />}
        <div className="flex justify-center mb-4">
          <h1 className="text-xl md:text-2xl font-bold tracking-tight">
            {ct.title}
          </h1>
        </div>
        <div className="container max-w-4xl mx-auto px-4">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg">{ct.messages.loadingTestsAdmin}</div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (adminTestsError) {
    return (
      <div>
        {showPageBack && <BtnNavigateBack label="Dashboard" />}
        <div className="flex justify-center mb-4">
          <h1 className="text-xl md:text-2xl font-bold tracking-tight">
            {ct.title}
          </h1>
        </div>
        <div className="container max-w-4xl mx-auto px-4">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-red-500">
              {ct.messages.errorLoadingAdmin}
              <button
                onClick={() => refetchAdminTests()}
                className="ml-2 underline"
              >
                {ct.messages.tryAgain}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleDownloadPdf = async (test: VehicleTest) => {
    setIsGeneratingPdf({
      isOpen: true,
      testName: test.vehicleName,
      fileName: `fordonsprotokoll-${test.vehicleDetails?.regNumber || test.id}.pdf`,
    });
    try {
      const testWithUrls = await resolveTestImageUrls(
        test,
        getDownloadUrlAsync,
      );
      await generateVehicleTestPdf(testWithUrls, language);
      toast.success(ct.messages.pdfGenerated);
    } catch (error) {
      toast.error(ct.messages.pdfError);
    } finally {
      setIsGeneratingPdf({ isOpen: false });
    }
  };

  const archiveToggleLabel = () => {
    if (isArchiveMode) {
      return isMobile ? ct.actions.exitArchiveMobile : ct.actions.exitArchive;
    }
    return isMobile ? ct.actions.archiveMobile : ct.actions.archive;
  };

  const toolbarControlClass =
    "h-9 text-sm min-h-9 w-full min-w-0 backdrop-blur";
  const actionBtnClass = cn(
    "h-9 min-h-9 px-3 text-sm gap-1.5 justify-center whitespace-nowrap",
    isMobile ? "flex-1 min-w-0 w-full" : "shrink-0 w-auto",
  );

  const renderArchiveNavButton = (opts?: { fullWidth?: boolean }) => (
    <ColoredOutlineButton
      type="button"
      variant="blue"
      tintedBackground
      size="sm"
      onClick={() => navigate("/admin/car-test/archive")}
      className={cn(
        "h-8 px-2.5 text-xs gap-1.5 justify-center whitespace-nowrap",
        opts?.fullWidth ? "flex-1 min-w-0 w-full" : "shrink-0 w-auto",
      )}
    >
      <Archive className={OUTLINE_TEXT_BUTTON_ICON_CLASS} strokeWidth={2.35} />
      <span className="truncate">
        {isMobile
          ? ((ct.archive as { buttonLabel?: string }).buttonLabel ?? "Arkiv")
          : ct.archive.title}
      </span>
    </ColoredOutlineButton>
  );

  const renderListActions = () => {
    const showConfirm = isArchiveMode && selectedTests.size > 0;
    return (
      <div
        className={cn(
          "flex gap-2 items-stretch",
          isMobile ? "w-full" : "w-auto shrink-0 flex-wrap justify-end",
        )}
      >
        <ColoredOutlineButton
          type="button"
          variant="neutral"
          tintedBackground
          size="sm"
          onClick={() => {
            setIsArchiveMode(!isArchiveMode);
            setSelectedTests(new Set());
          }}
          className={actionBtnClass}
        >
          <ListChecks
            className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
            strokeWidth={2.35}
          />
          <span className="truncate">{archiveToggleLabel()}</span>
        </ColoredOutlineButton>

        {showConfirm ? (
          <ColoredOutlineButton
            type="button"
            variant="success"
            tintedBackground
            size="sm"
            onClick={() => setShowArchiveDialog(true)}
            className={actionBtnClass}
          >
            <Check
              className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
              strokeWidth={2.35}
            />
            <span className="truncate">
              {ct.actions[
                isMobile ? "archiveSelectedMobile" : "archiveSelected"
              ].replace("{count}", String(selectedTests.size))}
            </span>
          </ColoredOutlineButton>
        ) : null}

        {showConfirm
          ? null
          : isMobile
            ? renderArchiveNavButton({ fullWidth: true })
            : renderArchiveNavButton()}
      </div>
    );
  };

  return (
    <div>
      {showPageBack && <BtnNavigateBack label="Dashboard" />}

      <div className="relative mb-4">
        {isMobile ? (
          <div className="flex justify-center items-center">
            <h1 className="text-xl font-bold tracking-tight">{ct.title}</h1>
          </div>
        ) : (
          <div className="flex justify-center items-center">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight">
              {ct.title}
            </h1>
          </div>
        )}
      </div>

      <div className="container max-w-4xl mx-auto px-4">
        <Tabs
          value={testTab}
          onValueChange={(value) => {
            setTestTab(value as "active" | "handled" | "draft");
            setSelectedTests(new Set()); // Clear selection when switching tabs
            setIsArchiveMode(false); // Exit archive mode when switching tabs
            setCurrentPage(1); // Reset to first page when switching tabs
          }}
          defaultValue="active"
          className="w-full"
        >
          <div className="relative mb-3 min-h-8">
            <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-2">
              <div aria-hidden className="min-w-0" />
              <TabsList className="backdrop-blur rounded-xl h-8 p-0.5 text-xs w-auto max-w-[min(100%,20rem)] justify-self-center">
                <TabsTrigger
                  value="active"
                  className={`rounded-lg h-7 px-3 text-xs ${
                    isDark
                      ? "data-[state=active]:bg-accent/70"
                      : "data-[state=active]:bg-accent"
                  }`}
                >
                  {ct.tabs.active}
                </TabsTrigger>
                <TabsTrigger
                  value="handled"
                  className={`rounded-lg h-7 px-3 text-xs ${
                    isDark
                      ? "data-[state=active]:bg-accent/70"
                      : "data-[state=active]:bg-accent"
                  }`}
                >
                  {ct.tabs.handled}
                </TabsTrigger>
                <TabsTrigger
                  value="draft"
                  className={`rounded-lg h-7 px-3 text-xs ${
                    isDark
                      ? "data-[state=active]:bg-accent/70"
                      : "data-[state=active]:bg-accent"
                  }`}
                >
                  {ct.tabs.draft}
                </TabsTrigger>
              </TabsList>
              {canManageVehicles ? (
                <div className="flex justify-end min-w-0">
                  <CreateTestButton onClick={handleCreateTest} />
                </div>
              ) : null}
            </div>
          </div>

          <TabsContent value="active">
            <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-1 min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap lg:flex-nowrap">
                <Input
                  placeholder={ct.search.placeholder}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className={cn(toolbarControlClass, "sm:flex-[2_1_12rem]")}
                />
                <div className="sm:w-[11rem] sm:flex-none min-w-0">
                  <Select
                    value={activeStatus}
                    onValueChange={(v) =>
                      setActiveStatus(
                        v as "all" | "pending-review" | "complement",
                      )
                    }
                  >
                    <SelectTrigger className={toolbarControlClass}>
                      <SelectValue placeholder={ct.filters.filterByStatus} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{ct.filters.all}</SelectItem>
                      <SelectItem value="pending-review">
                        {ct.filters.pendingReview}
                      </SelectItem>
                      <SelectItem value="complement">
                        {ct.filters.complement}
                      </SelectItem>
                      <SelectItem value="change-requested">
                        {ct.filters.changeRequested ?? "Ändring begärd"}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <CarTestSortSelect
                  value={testSort}
                  onValueChange={(v) => {
                    setTestSort(v);
                    setCurrentPage(1);
                  }}
                  className="sm:w-[11rem] sm:flex-none"
                  triggerClassName={toolbarControlClass}
                />
              </div>
              <div className="flex shrink-0 items-center gap-2 lg:pl-2">
                {renderListActions()}
              </div>
            </div>
            <div className="space-y-2">
              <AnimatePresence>
                {activeTests && activeTests.length > 0 ? (
                  activeTests
                    .filter((test) => filterTestByStatus(test, activeStatus))
                    .map((test) => (
                      <motion.div
                        key={test.id}
                        className={`relative ${
                          isArchiveMode ? "cursor-pointer" : ""
                        }`}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 200 }}
                        transition={{ duration: 0.5 }}
                        onClick={() => {
                          if (isArchiveMode) {
                            const newSelected = new Set(selectedTests);
                            if (newSelected.has(test.id)) {
                              newSelected.delete(test.id);
                            } else {
                              newSelected.add(test.id);
                            }
                            setSelectedTests(newSelected);
                          }
                        }}
                      >
                        <div
                          className={`flex items-start gap-3 ${
                            isArchiveMode ? "mb-2" : ""
                          }`}
                        >
                          {isArchiveMode && (
                            <input
                              type="checkbox"
                              checked={selectedTests.has(test.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                const newSelected = new Set(selectedTests);
                                if (e.target.checked) {
                                  newSelected.add(test.id);
                                } else {
                                  newSelected.delete(test.id);
                                }
                                setSelectedTests(newSelected);
                              }}
                              className="w-5 h-5 mt-2 flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <div className="flex-1">
                            <ActiveTest
                              key={test.vehicleDetails.regNumber}
                              test={test}
                              onEdit={
                                canManageVehicles ? handleEdit : undefined
                              }
                              onView={() => setViewTest(test)}
                              onDownload={handleDownloadPdf}
                              isAdmin={true}
                              canManageVehicles={canManageVehicles}
                              onApprove={
                                canManageVehicles
                                  ? () => handleStatusChange(test, "approved")
                                  : undefined
                              }
                              onReject={
                                canManageVehicles
                                  ? () => handleRejectClick(test)
                                  : undefined
                              }
                              onRequestComplement={
                                canManageVehicles
                                  ? () =>
                                      setComplementDialog({
                                        open: true,
                                        email: test.customer?.email || "",
                                        test,
                                      })
                                  : undefined
                              }
                              onConfirmChangeRequest={
                                canManageVehicles
                                  ? handleConfirmChangeRequest
                                  : undefined
                              }
                              isAnimating={!!animationStatus[test.id]}
                              isUpdating={isUpdatingTestStatus}
                              selectionMode={isArchiveMode}
                            />
                          </div>
                        </div>
                      </motion.div>
                    ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {ct.messages.noActiveTests}
                  </div>
                )}
              </AnimatePresence>
            </div>
            {testTab === "active" && totalAdminTests > 0 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalAdminTests / itemsPerPage)}
                  totalItems={totalAdminTests}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(next) => {
                    setItemsPerPage(next);
                    setCurrentPage(1);
                  }}
                  itemsPerPageOptions={[10, 25, 50]}
                />
              </div>
            )}
          </TabsContent>

          <TabsContent value="handled">
            <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-1 min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap lg:flex-nowrap">
                <Input
                  placeholder={ct.search.placeholder}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className={cn(toolbarControlClass, "sm:flex-[2_1_12rem]")}
                />
                <div className="sm:w-[11rem] sm:flex-none min-w-0">
                  <Select
                    value={handledStatus}
                    onValueChange={(v) =>
                      setHandledStatus(
                        v as "all" | "approved" | "rejected" | "active-listing",
                      )
                    }
                  >
                    <SelectTrigger className={toolbarControlClass}>
                      <SelectValue placeholder={ct.filters.statusPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{ct.filters.all}</SelectItem>
                      <SelectItem value="approved">
                        {ct.filters.approved}
                      </SelectItem>
                      <SelectItem value="rejected">
                        {ct.filters.rejected}
                      </SelectItem>
                      <SelectItem value="active-listing">
                        {ct.filters.listingCreated}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <CarTestSortSelect
                  value={testSort}
                  onValueChange={(v) => {
                    setTestSort(v);
                    setCurrentPage(1);
                  }}
                  className="sm:w-[11rem] sm:flex-none"
                  triggerClassName={toolbarControlClass}
                />
              </div>
              <div className="flex shrink-0 items-center gap-2 lg:pl-2">
                {renderListActions()}
              </div>
            </div>
            <div className="space-y-2">
              <AnimatePresence>
                {handledTests && handledTests.length > 0 ? (
                  handledTests
                    .filter((test) => filterTestByStatus(test, handledStatus))
                    .map((test) => (
                      <motion.div
                        key={test.id}
                        className={`relative ${
                          isArchiveMode ? "cursor-pointer" : ""
                        }`}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 200 }}
                        transition={{ duration: 0.5 }}
                        onClick={() => {
                          if (isArchiveMode) {
                            const newSelected = new Set(selectedTests);
                            if (newSelected.has(test.id)) {
                              newSelected.delete(test.id);
                            } else {
                              newSelected.add(test.id);
                            }
                            setSelectedTests(newSelected);
                          }
                        }}
                      >
                        <div
                          className={`flex items-start gap-3 ${
                            isArchiveMode ? "mb-2" : ""
                          }`}
                        >
                          {isArchiveMode && (
                            <input
                              type="checkbox"
                              checked={selectedTests.has(test.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                const newSelected = new Set(selectedTests);
                                if (e.target.checked) {
                                  newSelected.add(test.id);
                                } else {
                                  newSelected.delete(test.id);
                                }
                                setSelectedTests(newSelected);
                              }}
                              className="w-5 h-5 mt-2 flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <div className="flex-1">
                            <ActiveTest
                              key={test.vehicleDetails.regNumber}
                              test={test}
                              onEdit={
                                canManageVehicles ? handleEdit : undefined
                              }
                              onView={() => setViewTest(test)}
                              onDownload={handleDownloadPdf}
                              onCreateAd={
                                canManageVehicles && canViewBidsOverview
                                  ? () => handleCreateAdFromTest(test)
                                  : undefined
                              }
                              isAdmin={true}
                              canManageVehicles={canManageVehicles}
                              isUpdating={isUpdatingTestStatus}
                              selectionMode={isArchiveMode}
                            />
                          </div>
                        </div>
                      </motion.div>
                    ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {ct.messages.noHandledTests}
                  </div>
                )}
              </AnimatePresence>
            </div>
            {testTab === "handled" && totalAdminTests > 0 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalAdminTests / itemsPerPage)}
                  totalItems={totalAdminTests}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(next) => {
                    setItemsPerPage(next);
                    setCurrentPage(1);
                  }}
                  itemsPerPageOptions={[10, 25, 50]}
                />
              </div>
            )}
          </TabsContent>

          <TabsContent value="draft">
            <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-1 min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap lg:flex-nowrap">
                <Input
                  placeholder={ct.search.placeholder}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className={cn(toolbarControlClass, "sm:flex-[2_1_12rem]")}
                />
                <CarTestSortSelect
                  value={testSort}
                  onValueChange={(v) => {
                    setTestSort(v);
                    setCurrentPage(1);
                  }}
                  className="sm:w-[11rem] sm:flex-none"
                  triggerClassName={toolbarControlClass}
                />
              </div>
              <div className="flex shrink-0 items-center gap-2 lg:pl-2">
                {renderListActions()}
              </div>
            </div>
            <div className="space-y-2">
              {isLoadingDraftTests ? (
                <div className="text-center py-8 text-gray-500">
                  {ct.messages.loadingDrafts}
                </div>
              ) : draftTests && draftTests.length > 0 ? (
                <AnimatePresence>
                  {draftTests
                    .filter((test) => {
                      if (!searchQuery) return true;
                      const searchLower = searchQuery.toLowerCase();
                      const regNumber =
                        test.vehicleDetails?.regNumber?.toLowerCase() || "";
                      const make =
                        test.vehicleDetails?.make?.toLowerCase() || "";
                      const model =
                        test.vehicleDetails?.model?.toLowerCase() || "";
                      return (
                        regNumber.includes(searchLower) ||
                        `${make} ${model}`.includes(searchLower)
                      );
                    })
                    .map((test) => (
                      <motion.div
                        key={test.id}
                        className={`relative ${
                          isArchiveMode ? "cursor-pointer" : ""
                        }`}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 200 }}
                        transition={{ duration: 0.5 }}
                        onClick={() => {
                          if (isArchiveMode) {
                            const newSelected = new Set(selectedTests);
                            if (newSelected.has(test.id)) {
                              newSelected.delete(test.id);
                            } else {
                              newSelected.add(test.id);
                            }
                            setSelectedTests(newSelected);
                          }
                        }}
                      >
                        <div
                          className={`flex items-start gap-3 ${
                            isArchiveMode ? "mb-2" : ""
                          }`}
                        >
                          {isArchiveMode && (
                            <input
                              type="checkbox"
                              checked={selectedTests.has(test.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                const newSelected = new Set(selectedTests);
                                if (e.target.checked) {
                                  newSelected.add(test.id);
                                } else {
                                  newSelected.delete(test.id);
                                }
                                setSelectedTests(newSelected);
                              }}
                              className="w-5 h-5 mt-2 flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <div className="flex-1">
                            <ActiveTest
                              key={test.vehicleDetails.regNumber}
                              test={test}
                              onEdit={
                                canManageVehicles ? handleEdit : undefined
                              }
                              onView={() => setViewTest(test)}
                              onDownload={handleDownloadPdf}
                              isAdmin={true}
                              canManageVehicles={canManageVehicles}
                              isUpdating={isUpdatingTestStatus}
                              selectionMode={isArchiveMode}
                            />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                </AnimatePresence>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {ct.messages.noDraftTests}
                </div>
              )}
            </div>
            {testTab === "draft" && totalDraftTests > 0 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalDraftTests / itemsPerPage)}
                  totalItems={totalDraftTests}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(next) => {
                    setItemsPerPage(next);
                    setCurrentPage(1);
                  }}
                  itemsPerPageOptions={[10, 25, 50]}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <EditTestDialog
        test={selectedTest}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <CreateTestDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      <Dialog open={!!viewTest} onOpenChange={() => setViewTest(null)}>
        <DialogContent
          expandable={isNativeApp}
          sheetOpen={!!viewTest}
          className={cn(
            "rounded-lg max-w-3xl max-h-[75vh] sm:max-h-[80vh] overflow-hidden flex flex-col p-0",
            isNativeApp && "native-test-sheet native-sheet-expandable max-w-none w-full",
          )}
        >
          <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4">
            <DialogTitle>
              {viewTest
                ? [
                    viewTest.vehicleDetails?.make,
                    viewTest.vehicleDetails?.model,
                    viewTest.vehicleDetails?.year,
                  ]
                    .filter(Boolean)
                    .join(" ") || ct.dialogs.view.title
                : ct.dialogs.view.title}
            </DialogTitle>
          </DialogHeader>
          {viewTest && (
            <div className="px-4 sm:px-6 pb-6 overflow-y-auto flex-1 min-h-0">
              <VehicleTestDocumentView
                data={mapVehicleTestToDocumentData(viewTest)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={complementDialog.open}
        onOpenChange={() => {
          setComplementDialog({ open: false, email: "", test: null });
          setComplementMsg("");
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{ct.dialogs.requestComplement.title}</DialogTitle>
          </DialogHeader>
          {complementDialog.email && (
            <div className="mb-4 text-sm text-muted-foreground">
              {ct.dialogs.requestComplement.messageToCustomerLabel}{" "}
              <span className="font-semibold text-foreground">
                {complementDialog.email}
              </span>
            </div>
          )}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {ct.dialogs.requestComplement.messageToCustomer}
            </label>
            <Textarea
              className="w-full min-h-[140px] resize-y"
              placeholder={ct.dialogs.requestComplement.placeholder}
              value={complementMsg}
              onChange={(e) => setComplementMsg(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setComplementDialog({ open: false, email: "", test: null });
                setComplementMsg("");
              }}
            >
              {ct.dialogs.requestComplement.cancel}
            </Button>
            <Button
              variant="default"
              onClick={async () => {
                if (!complementDialog.test || !complementMsg.trim()) return;
                try {
                  await handleStatusChange(
                    complementDialog.test,
                    "complement",
                    complementMsg,
                  );
                  setComplementDialog({ open: false, email: "", test: null });
                  setComplementMsg("");
                } catch (error) {
                  // Error is already handled in handleStatusChange
                }
              }}
              disabled={!complementMsg.trim() || isUpdatingTestStatus}
            >
              {isUpdatingTestStatus
                ? ct.dialogs.requestComplement.sending
                : ct.dialogs.requestComplement.send}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CreateListingDialog for approved tests */}
      {selectedVehicleForListing && (
        <CreateListingDialog
          open={showCreateListing}
          onOpenChange={setShowCreateListing}
          preSelectedVehicle={selectedVehicleForListing}
          onSuccess={handleCreateListingSuccess}
        />
      )}

      {/* PDF Generation Dialog */}
      <PdfGenerationDialog
        isOpen={isGeneratingPdf.isOpen}
        testName={isGeneratingPdf.testName}
        fileName={isGeneratingPdf.fileName}
      />

      {/* Reject Reason Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{ct.dialogs.reject.title}</DialogTitle>
            <DialogDescription>
              {ct.dialogs.reject.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="reject-reason" className="text-sm font-medium">
                {ct.dialogs.reject.reasonLabel}{" "}
                <span className="text-red-500">
                  {ct.dialogs.reject.reasonRequired}
                </span>
              </label>
              <Textarea
                id="reject-reason"
                placeholder={ct.dialogs.reject.placeholder}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="min-h-[120px]"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectDialog(false);
                setRejectReason("");
                setTestToReject(null);
              }}
            >
              {ct.dialogs.reject.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectConfirm}
              disabled={!rejectReason.trim() || isUpdatingTestStatus}
            >
              {isUpdatingTestStatus
                ? ct.dialogs.reject.rejecting
                : ct.dialogs.reject.reject}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{ct.dialogs.archive.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              {ct.dialogs.archive.message
                .replace("{count}", String(selectedTests.size))
                .replace(
                  "{plural}",
                  selectedTests.size !== 1 ? ct.plural.tests : ct.plural.test,
                )}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowArchiveDialog(false);
                  setSelectedTests(new Set());
                }}
              >
                {ct.dialogs.archive.cancel}
              </Button>
              <Button
                onClick={async () => {
                  try {
                    await archiveCarTests(Array.from(selectedTests));
                    toast.success(
                      ct.messages.testsArchived
                        .replace("{count}", String(selectedTests.size))
                        .replace(
                          "{plural}",
                          selectedTests.size !== 1
                            ? ct.plural.tests
                            : ct.plural.test,
                        ),
                    );
                    setSelectedTests(new Set());
                    setShowArchiveDialog(false);
                  } catch (error) {
                    toast.error(ct.messages.archiveError);
                  }
                }}
                disabled={isArchivingCarTests || selectedTests.size === 0}
              >
                {isArchivingCarTests
                  ? ct.dialogs.archive.archiving
                  : ct.dialogs.archive.archive}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCarTest;
